import React from "react";
import { useQuery } from "@tanstack/react-query";
import MapboxComponent from "../MapboxComponent";
import { geocodeAddress } from "../../../helpers/quickAddProperty";
import "./PropertyLocationMap.scss";

const APPROXIMATE_RADIUS_MILES = 0.75;
const AREA_ZOOM = 10;

const usableNumber = (value) => {
  const number = Number(value);
  return value !== null && value !== "" && Number.isFinite(number) && number !== 0 ? number : null;
};

const centerFrom = (coordinates) => {
  const lng = usableNumber(coordinates?.[0]);
  const lat = usableNumber(coordinates?.[1]);
  return lng !== null && lat !== null ? [lng, lat] : null;
};

const roughly = (center) =>
  center ? center.map((value) => Math.round(value * 100) / 100) : null;

const lookUp = async (query) => {
  const result = await geocodeAddress(query);
  return centerFrom([result?.geometry?.location?.lng, result?.geometry?.location?.lat]);
};

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const PropertyLocationMap = ({
  mapImg,
  coordinates,
  address,
  area,
  precise: preciseProp = true,
  className = "",
  renderFrame,
}) => {
  const frame = (node) => (renderFrame ? renderFrame(node) : <div className={className}>{node}</div>);
  // The API masks a hidden location by replacing the street with the area label.
  // An "address" that is only the area is not a home, so never pin it.
  const precise = preciseProp && !(area && sameText(address, area));
  const shownImage = precise ? mapImg : null;
  const ownCenter = centerFrom(coordinates);
  const place = String((precise ? address : area) || "").trim();
  const needsLookup = !shownImage && !ownCenter && Boolean(place);

  const lookup = useQuery({
    queryKey: ["propertyLocationLookup", precise ? "address" : "area", place],
    queryFn: () => lookUp(place),
    enabled: needsLookup,
    staleTime: Infinity,
    retry: false,
  });

  if (shownImage) {
    return frame(<img src={shownImage + "?" + Math.random().toString(36)} alt="Location map" />);
  }

  const found = ownCenter || lookup.data || null;
  if (found) {
    return frame(
      precise ? (
        <MapboxComponent center={found} height="100%" />
      ) : (
        // A circle only when it is drawn around the property's own (rounded)
        // coordinates. A looked-up city or ZIP centre says nothing about where
        // the home is, so that case just shows the area.
        <MapboxComponent
          center={roughly(found)}
          height="100%"
          radiusMiles={ownCenter ? APPROXIMATE_RADIUS_MILES : undefined}
          zoom={ownCenter ? undefined : AREA_ZOOM}
          showMarker={false}
          showResetButton={false}
        />
      ),
    );
  }

  if (needsLookup && lookup.isLoading) {
    return frame(<div className="property-location-map--pending" aria-hidden="true" />);
  }

  return place ? <p className="property-location-map__address">{place}</p> : null;
};

export default PropertyLocationMap;
